import { lazy, Suspense, useState, useRef, useEffect } from 'react';

import { Background, useReducedMotion } from './Effects';
import { MarketingFooter, MarketingHeader } from './MarketingChrome';
import { InspectFeature } from './InspectFeature';
import type { ConversionScreen } from './PricingEvaluationOverlay';
import dynamic from 'next/dynamic';
const DemoRequestOverlay = dynamic(
  () =>
    import('./DemoRequestOverlay').then((module) => module.DemoRequestOverlay),
  { ssr: false },
);
import { useRouter } from 'next/compat/router';
import { Copy } from './CopyEditor';
import { deferUntilPageSettles } from './performance';
import { MARKETING_LIMITS } from '~/lib/constants/limits';
import { getMarketingCheckoutReturnUrl } from '~/lib/marketing/checkout-intent';
import { signaturePricingEnabled } from '~/lib/marketing/signature-pricing';

const SignaturePricingOverlay = lazy(() =>
  import('./SignaturePricingOverlay').then((module) => ({
    default: module.SignaturePricingOverlay,
  })),
);

const ignoreBackgroundStatus = () => undefined;

const PricingEvaluationOverlay = lazy(() =>
  import('./PricingEvaluationOverlay').then((module) => ({
    default: module.PricingEvaluationOverlay,
  })),
);

const proofLogos = [
  {
    name: 'TEDx',
    src: '/landing/assets/logos/tedx-delivery.webp',
    variant: 'tedx',
  },
  {
    name: 'Microsoft',
    src: '/landing/assets/logos/microsoft-symbol.svg',
    variant: 'microsoft',
  },
  {
    name: 'ThoughtFull',
    src: '/landing/assets/logos/thoughtfull.svg',
    variant: 'thoughtfull',
  },
  {
    name: 'Fortune 100',
    src: '/landing/assets/logos/fortune-100-transparent.webp',
    variant: 'fortune',
    srcSet:
      '/landing/assets/logos/fortune-100-delivery-128.webp 128w, /landing/assets/logos/fortune-100-transparent.webp 192w',
    sizes: '(max-width: 800px) 69px, 77px',
  },
  {
    name: 'Glowforge',
    src: '/landing/assets/logos/glowforge.svg',
    variant: 'glowforge',
  },
  {
    name: 'NYU Stern School of Business',
    src: '/landing/assets/logos/nyu-stern-delivery.webp',
    variant: 'nyu',
  },
];
const testimonials = [
  {
    quote:
      'The client changed the brief two days before the pitch. One person rebuilt the story without another long round of late-night revisions and with the same quality.',
    credit: 'Lena, Creative Director',
  },
  {
    quote:
      "Sales, product, and leadership were all working from different slide templates but we walked into launch day and people audibly 'wow\'d at our presentation.",
    credit: 'Priya, VP of Marketing',
  },
  {
    quote:
      "Our decks are dense and I'm not a designer so I don't usually enjoy making them but DirectorDeck is a lot better than the other platforms I've tried.",
    credit: 'Nia, Investor Relations Manager',
  },
];

const deckVideos = [
  {
    webm: '/landing/assets/decks/lgbtqia-nonprofit-web.webm?v=20260930-perf1',
    mp4: '/landing/assets/decks/lgbtqia-nonprofit-web.mp4?v=20260930-perf1',
    poster: '/landing/assets/decks/lgbtqia-nonprofit-poster.jpg',
    label: 'LGBTQIA nonprofit presentation',
  },
  {
    webm: '/landing/assets/decks/alpine-creator-campaign-web.webm?v=20260930-perf1',
    mp4: '/landing/assets/decks/alpine-creator-campaign-web.mp4?v=20260930-perf1',
    poster: '/landing/assets/decks/alpine-creator-campaign-poster.jpg',
    label: 'Alpine creator campaign presentation',
  },
  {
    webm: '/landing/assets/decks/emea-expansion-briefing-web.webm?v=20260930-perf1',
    mp4: '/landing/assets/decks/emea-expansion-briefing-web.mp4?v=20260930-perf1',
    poster: '/landing/assets/decks/emea-expansion-briefing-poster.jpg',
    label: 'EMEA expansion briefing presentation',
  },
  {
    webm: '/landing/assets/decks/hudson-spire-progress-report-web.webm?v=20260930-perf1',
    mp4: '/landing/assets/decks/hudson-spire-progress-report-web.mp4?v=20260930-perf1',
    poster: '/landing/assets/decks/hudson-spire-progress-report-poster.jpg',
    label: 'Hudson Spire progress report presentation',
  },
];

const audiences = [
  'Founders',
  'Pitch Teams',
  'Executives',
  'Thought Leaders',
  'Higher Education',
  'Marketing Teams',
  'Creative Agencies',
  'Real Estate Developers',
  'Technology Leaders',
  'Nonprofits',
  'Consultants',
];

const faqs = [
  {
    question: 'What can I start with?',
    answer:
      'Bring an existing deck, a rough outline, or source material. DirectorDeck can meet the story wherever it currently lives.',
  },
  {
    question: 'Can I use my existing brand system?',
    answer:
      'Yes. Your typography, color, imagery, and visual standards can guide the presentation from the first slide to the last.',
  },
  {
    question: 'Do I need design experience?',
    answer:
      'No. The experience is designed to help teams shape a clear, polished presentation without starting from a blank canvas.',
  },
  {
    question: 'How do teams review a presentation?',
    answer:
      'Keep the story, feedback, and visual direction together so stakeholders can respond to one coherent working draft.',
  },
  {
    question: 'What happens to my original slides?',
    answer:
      'They remain the foundation of the work, preserving the ideas and information that already matter to your team.',
  },
];

function HeroVideo({ paused, reduced }: { paused: boolean; reduced: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const poster = useRef<HTMLImageElement>(null);
  const [stream, setStream] = useState(false);
  const [readyPoster, setReadyPoster] = useState<string>();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const image = poster.current;
    if (image?.complete && image.naturalWidth > 0)
      setReadyPoster(image.currentSrc || image.src);
  }, []);

  useEffect(() => {
    const player = video.current;
    if (!player) return;
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry.isIntersecting),
    );
    observer.observe(player);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reduced) {
      setStream(false);
      return;
    }
    if (!visible || stream || !readyPoster) return;
    return deferUntilPageSettles(() => setStream(true), 1200, 2500);
  }, [reduced, visible, stream, readyPoster]);

  useEffect(() => {
    const player = video.current;
    if (!player) return;
    if (stream) player.load();
  }, [stream]);

  useEffect(() => {
    const player = video.current;
    if (!player) return;
    const update = () => {
      if (!stream || paused || reduced || !visible || document.hidden)
        player.pause();
      else void player.play().catch(() => undefined);
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [paused, reduced, stream, visible]);

  return (
    <div className="hero-video" aria-hidden="true">
      <img
        ref={poster}
        className="hero-poster"
        src="/landing/assets/hudson-hero-demo-poster-960.webp"
        srcSet="/landing/assets/hudson-hero-demo-poster-640.webp 640w, /landing/assets/hudson-hero-demo-poster-768.webp 768w, /landing/assets/hudson-hero-demo-poster-960.webp 960w, /landing/assets/hudson-hero-demo-poster-1280.webp 1280w, /landing/assets/hudson-hero-demo-poster-web.webp 1600w"
        alt=""
        width="1600"
        height="900"
        loading="eager"
        {...{ fetchpriority: 'high' }}
        sizes="(max-width: 390px) calc(100vw - 36px), (max-width: 800px) calc(100vw - 44px), (max-width: 1440px) 66vw, 950px"
        onLoad={(event) =>
          setReadyPoster(
            event.currentTarget.currentSrc || event.currentTarget.src,
          )
        }
      />
      <video
        ref={video}
        muted
        loop
        playsInline
        preload={stream ? 'metadata' : 'none'}
        poster={readyPoster}
        style={{ opacity: readyPoster && !reduced ? 1 : 0 }}
      >
        {stream && (
          <source
            src="/landing/assets/hudson-hero-demo.webm?v=20260930-perf1"
            type="video/webm"
          />
        )}
        {stream && (
          <source
            src="/landing/assets/hudson-hero-demo-web.mp4?v=20260930-perf1"
            type="video/mp4"
          />
        )}
      </video>
    </div>
  );
}

function DeckVideoCard({
  video,
  paused,
  reduced,
  duplicate,
  index,
}: {
  video: (typeof deckVideos)[number];
  paused: boolean;
  reduced: boolean;
  duplicate: boolean;
  index: number;
}) {
  const card = useRef<HTMLElement>(null);
  const player = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = card.current;
    if (!element) return;
    const loadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setReady(true);
        loadObserver.disconnect();
      },
      { rootMargin: '180px' },
    );
    const playObserver = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.05);
      },
      { threshold: [0, 0.05] },
    );
    loadObserver.observe(element);
    playObserver.observe(element);
    return () => {
      loadObserver.disconnect();
      playObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const media = player.current;
    if (!media) return;
    const update = () => {
      if (!visible || paused || reduced || document.hidden) media.pause();
      else void media.play().catch(() => undefined);
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [paused, ready, reduced, visible]);

  return (
    <figure
      ref={card}
      className={`deck-video-card deck-video-card-${index + 1}`}
      tabIndex={duplicate ? -1 : 0}
      aria-label={duplicate ? undefined : video.label}
    >
      {ready && !reduced ? (
        <video
          ref={player}
          muted
          loop
          playsInline
          preload="none"
          poster={video.poster}
          aria-hidden="true"
        >
          <source src={video.webm} type="video/webm" />
          <source src={video.mp4} type="video/mp4" />
        </video>
      ) : (
        <img
          src={video.poster}
          alt=""
          loading="lazy"
          decoding="async"
          width="960"
          height="540"
          aria-hidden="true"
        />
      )}
    </figure>
  );
}

function DeckMarquee({
  paused,
  reduced,
}: {
  paused: boolean;
  reduced: boolean;
}) {
  const marquee = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const section = marquee.current;
    if (!section) return;
    const visibility = new IntersectionObserver(
      ([entry]) =>
        setVisible(entry.isIntersecting && entry.intersectionRatio > 0),
      { threshold: [0, 0.05] },
    );
    visibility.observe(section);
    return () => visibility.disconnect();
  }, []);

  return (
    <section
      ref={marquee}
      className={`deck-showcase section-rule${paused || reduced || !visible ? ' is-paused' : ''}`}
      id="examples"
      aria-labelledby="deck-showcase-title"
    >
      <h2 className="deck-showcase-heading wrap" id="deck-showcase-title">
        <Copy id="decks.heading">Created with</Copy>
        <img
          src="/landing/assets/logos/directordeck-alt-wordmark-delivery.webp"
          alt="DirectorDeck"
          width="1200"
          height="232"
        />
      </h2>
      <div className="deck-marquee">
        <div className="deck-video-track">
          {[0, 1].map((group) => (
            <div
              className="deck-video-group"
              key={group}
              aria-hidden={group === 1}
            >
              {deckVideos.map((video, index) => (
                <DeckVideoCard
                  video={video}
                  paused={paused || !visible}
                  reduced={reduced}
                  duplicate={group === 1}
                  index={index}
                  key={`${group}-${video.label}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AudienceShowcase({
  paused,
  onBuild,
}: {
  paused: boolean;
  onBuild: () => void;
}) {
  return (
    <section
      className={`audience-showcase wrap section-rule${paused ? ' is-paused' : ''}`}
      aria-labelledby="audience-title"
    >
      <div className="audience-copy">
        <h2 id="audience-title">
          <Copy id="audience.heading">Start giving great presentations</Copy>
        </h2>
        <div className="audience-actions">
          <button className="solid-cta" type="button" onClick={onBuild}>
            <Copy id="audience.cta">Build a Deck Now</Copy>
            <span className="solid-cta-arrow" aria-hidden="true">
              ↗
            </span>
          </button>
        </div>
      </div>
      <div className="audience-ticker">
        <div className="audience-track" aria-hidden="true">
          {[0, 1].map((group) => (
            <div
              className="audience-list"
              aria-hidden={group === 1}
              key={group}
            >
              {audiences.map((audience, index) => (
                <span key={`${group}-${audience}`}>
                  <Copy id={`audience.item.${index}`}>{audience}</Copy>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
      <p className="sr-only">
        DirectorDeck is built for {audiences.join(', ')}.
      </p>
    </section>
  );
}

export default function LandingPage() {
  const routeEvents = useRouter()?.events;
  const reduced = useReducedMotion();
  const [menu, setMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [conversionScreen, setConversionScreen] =
    useState<ConversionScreen>('home');
  const [linkedSeats, setLinkedSeats] = useState<number>(
    MARKETING_LIMITS.MIN_STUDIO_SEATS,
  );
  const menuButton = useRef<HTMLButtonElement>(null);
  const conversionOpener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const openLinkedScreen = () => {
      const params = new URLSearchParams(window.location.search);
      const screen =
        params.get('mode') === 'evaluation'
          ? 'evaluation'
          : window.location.hash === '#pricing' ||
              (signaturePricingEnabled() && params.get('pricing') === 'refined')
            ? 'pricing'
            : undefined;
      if (!screen) return;
      const returnUrls = params.getAll('returnUrl');
      const returnUrl = getMarketingCheckoutReturnUrl(
        returnUrls.length === 1 ? returnUrls[0] : undefined,
      );
      const quantities = params.getAll('seats');
      const seatIntent =
        returnUrl ??
        getMarketingCheckoutReturnUrl(
          quantities.length === 1
            ? `/studio-checkout?seats=${quantities[0]}`
            : undefined,
        );
      setLinkedSeats(
        seatIntent
          ? Number(seatIntent.split('=')[1])
          : MARKETING_LIMITS.MIN_STUDIO_SEATS,
      );
      conversionOpener.current = document.activeElement as HTMLElement;
      setConversionScreen(screen);
      if (screen === 'pricing') {
        if (signaturePricingEnabled()) params.delete('pricing');
        const search = params.toString();
        history.replaceState(
          history.state,
          '',
          window.location.pathname + (search ? `?${search}` : ''),
        );
      }
    };
    openLinkedScreen();
    window.addEventListener('hashchange', openLinkedScreen);
    routeEvents?.on('routeChangeComplete', openLinkedScreen);
    return () => {
      window.removeEventListener('hashchange', openLinkedScreen);
      routeEvents?.off('routeChangeComplete', openLinkedScreen);
    };
  }, [routeEvents]);
  const start = () => window.location.assign('/auth/sign-in');
  const showPricing = () => {
    conversionOpener.current = menu
      ? menuButton.current
      : (document.activeElement as HTMLElement);
    setConversionScreen('pricing');
    setMenu(false);
  };
  const showEvaluation = () => {
    conversionOpener.current = document.activeElement as HTMLElement;
    setConversionScreen('evaluation');
    setMenu(false);
  };
  return (
    <div className="site">
      <Background
        enabled={!reduced}
        paused={false}
        quality="auto"
        onStatus={ignoreBackgroundStatus}
      />
      <MarketingHeader
        home
        menu={menu}
        menuButton={menuButton}
        onMenu={() => setMenu(!menu)}
        onNav={() => setMenu(false)}
        onLogin={start}
        onPricing={showPricing}
        onEvaluation={showEvaluation}
        flareEnabled={!reduced}
        paused={false}
      />

      <main>
        <section className="hero wrap" id="product">
          <div className="hero-copy">
            <h1>
              <Copy id="hero.heading">
                Make presentations just like the ones you had in your head, only
                better
              </Copy>
            </h1>
            <div className="hero-actions">
              <a className="solid-cta watch-examples" href="#examples">
                <Copy id="hero.cta">Learn More</Copy>
              </a>
            </div>
          </div>
          <HeroVideo paused={false} reduced={reduced} />
        </section>

        <section
          className="proof section-rule"
          id="teams"
          aria-labelledby="proof-title"
        >
          <p id="proof-title" className="proof-kicker">
            <Copy id="proof.kicker">As seen at</Copy>
          </p>
          <div
            className={`logo-marquee${reduced ? ' is-paused' : ''}`}
            aria-label="Organizations featuring DirectorDeck"
          >
            <div className="logo-track">
              {[0, 1].map((group) => (
                <div
                  className="logo-group"
                  key={group}
                  aria-hidden={group === 1}
                >
                  {proofLogos.map((logo) => (
                    <span
                      className={`logo-mark logo-mark--${logo.variant}`}
                      role={group === 0 ? 'img' : undefined}
                      aria-label={group === 0 ? logo.name : undefined}
                      key={`${group}-${logo.name}`}
                    >
                      <img
                        src={logo.src}
                        srcSet={logo.srcSet}
                        sizes={logo.sizes}
                        alt=""
                        aria-hidden="true"
                      />
                      {logo.variant === 'microsoft' && (
                        <span aria-hidden="true">Microsoft</span>
                      )}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="testimonials wrap">
            {testimonials.map((item, index) => (
              <blockquote className="testimonial-card" key={item.quote}>
                <p>
                  “<Copy id={`testimonial.${index}.quote`}>{item.quote}</Copy>”
                </p>
                <footer>
                  <Copy id={`testimonial.${index}.credit`}>{item.credit}</Copy>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <AudienceShowcase paused={false} onBuild={showEvaluation} />
        <InspectFeature paused={false} />
        <DeckMarquee paused={false} reduced={reduced} />

        <section
          className="faq wrap section-rule"
          id="faq"
          aria-labelledby="faq-title"
        >
          <div className="faq-list">
            {faqs.map((item, index) => {
              const open = openFaq === index;
              return (
                <div className="faq-item" data-open={open} key={item.question}>
                  <button
                    className="faq-question"
                    id={`faq-question-${index}`}
                    aria-expanded={open}
                    aria-controls={`faq-answer-${index}`}
                    onClick={() => setOpenFaq(open ? null : index)}
                  >
                    <span>
                      <span className="faq-number">0{index + 1}</span>
                      <Copy id={`faq.${index}.question`}>{item.question}</Copy>
                    </span>
                    <span className="faq-icon" aria-hidden="true">
                      +
                    </span>
                  </button>
                  <div
                    className="faq-answer"
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    aria-hidden={!open}
                  >
                    <div>
                      <p>
                        <Copy id={`faq.${index}.answer`}>{item.answer}</Copy>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="faq-intro">
            <h2 id="faq-title">
              <Copy id="faq.heading.line1">Frequently</Copy>
              <br />
              <Copy id="faq.heading.line2">asked questions.</Copy>
            </h2>
          </div>
        </section>
        <section className="closing wrap section-rule">
          <img src="/landing/assets/mark.svg" alt="" />
          <h2>
            <Copy id="closing.heading">
              Give it the presentation it deserves.
            </Copy>
          </h2>
          <button className="solid-cta" onClick={showEvaluation}>
            <Copy id="closing.cta">Get Started</Copy>
            <span className="solid-cta-arrow" aria-hidden="true">
              ↗
            </span>
          </button>
        </section>
      </main>
      <MarketingFooter />

      <DemoRequestOverlay
        reducedMotion={reduced}
        triggerClassName="demo-float--selected"
      />

      {conversionScreen !== 'home' && (
        <Suspense fallback={null}>
          {signaturePricingEnabled() && conversionScreen === 'pricing' ? (
            <SignaturePricingOverlay
              onClose={() => setConversionScreen('home')}
              onEvaluate={() => setConversionScreen('evaluation')}
              onContact={() => window.location.assign('/contact')}
              returnFocusTo={conversionOpener.current}
            />
          ) : (
            <PricingEvaluationOverlay
              key={linkedSeats}
              initialSeats={linkedSeats}
              screen={conversionScreen}
              onNavigate={setConversionScreen}
              onClose={() => setConversionScreen('home')}
              returnFocusTo={conversionOpener.current}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}
